import { UserProfile } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractUserProfile = (responseData: any): UserProfile | null => {
    if (!responseData) return null;

    // Sanitize a value: strip 'undefined', 'null', 'N/A', etc.
    const clean = (val: any): string => {
        if (val === undefined || val === null) return '';
        const s = String(val).trim();
        if (s === '' || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null' || s === 'N/A') return '';
        return s;
    };

    // Look for nested data structure first
    const dataContainer = responseData.data || responseData;

    // Case 1: Enrollment/Creation response structure
    if (dataContainer.healthIdNumber || dataContainer.healthId) {
        return {
            fullName: clean(dataContainer.name) || clean(dataContainer.fullName),
            abhaNumber: clean(dataContainer.healthIdNumber) || clean(dataContainer.healthId),
            abhaAddress: clean(dataContainer.abhaAddress),
            status: 'ACTIVE',
            kycStatus: 'VERIFIED',
            mobileVerified: true,
            mobile: clean(dataContainer.mobile),
            address: clean(dataContainer.address),
            profilePhoto: undefined,
            dob: dataContainer.dob || (dataContainer.yearOfBirth ? `${dataContainer.yearOfBirth}-${String(dataContainer.monthOfBirth || '1').padStart(2, '0')}-${String(dataContainer.dayOfBirth || '1').padStart(2, '0')}` : undefined),
            gender: clean(dataContainer.gender),
            email: clean(dataContainer.email),
            town: clean(dataContainer.town),
            district: clean(dataContainer.districtName) || clean(dataContainer.district),
            state: clean(dataContainer.stateName) || clean(dataContainer.state),
        };
    }

    // Case 2: Verification/Login response structure (users/accounts array)
    const userData = dataContainer.users?.[0] ||
        dataContainer.accounts?.[0] ||
        dataContainer.profile ||
        dataContainer.user ||
        dataContainer;

    const idValue = clean(userData.abhaNumber) || clean(userData.ABHANumber) || clean(userData.healthIdNumber) || clean(userData.healthId);
    const addrValue = clean(userData.abhaAddress) || clean(userData.preferredAbhaAddress);

    // Accept profile if it has an ABHA number OR at least an ABHA address (non-KYC profiles)
    if (userData && (idValue || addrValue)) {
        // improved name composition
        const composedName = [clean(userData.firstName), clean(userData.middleName), clean(userData.lastName)].filter(Boolean).join(' ');

        // improved DOB composition with padding
        let formattedDob = clean(userData.dob) || clean(userData.birthdate);
        if (!formattedDob && userData.yearOfBirth) {
            const y = userData.yearOfBirth;
            const m = userData.monthOfBirth ? String(userData.monthOfBirth).padStart(2, '0') : '01';
            const d = userData.dayOfBirth ? String(userData.dayOfBirth).padStart(2, '0') : '01';
            formattedDob = `${y}-${m}-${d}`;
        }

        return {
            fullName: clean(userData.fullName) || clean(userData.name) || composedName,
            abhaNumber: idValue,
            abhaAddress: clean(userData.abhaAddress) || clean(userData.preferredAbhaAddress),
            status: userData.status || 'ACTIVE',
            kycStatus: userData.kycStatus || (userData.kycVerified === 'true' || userData.kycVerified === true ? 'VERIFIED' : 'PENDING'),
            mobileVerified: userData.mobileVerified || !!userData.mobile,
            mobile: clean(userData.mobile) || clean(userData.mobileNumber),
            address: clean(userData.address) || [clean(userData.house), clean(userData.landmark), clean(userData.location)].filter(Boolean).join(', '),
            profilePhoto: userData.profilePhoto || userData.photo || userData.profile_photo,
            dob: formattedDob,
            gender: clean(userData.gender),
            email: clean(userData.email),
            town: clean(userData.town) || clean(userData.village) || clean(userData.city),
            district: clean(userData.districtName) || clean(userData.district),
            state: clean(userData.stateName) || clean(userData.state),
        };
    }

    return null;
};

export const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays as BlobPart[], { type: contentType });
};

export const getFlowDescription = (type: string, method: string): string => {
    const flows: { [key: string]: string } = {
        'enrollment-aadhaar': 'Create new ABHA using Aadhaar',
        'enrollment-mobile': 'Create new ABHA using Mobile',
        'abha-number-aadhaar': 'Verify ABHA using Aadhaar',
        'abha-number-mobile': 'Verify ABHA Number with Mobile',
        'abha-address-mobile': 'Login with ABHA Address',
        'communication-mobile-mobile': 'Verify Communication Mobile',
        'communication-mobile-user-mobile': 'Verify Communication Mobile with User Selection', // 👈 New entry
        'aadhaar-login-aadhaar': 'Verify ABHA by Aadhaar',
    };
    return flows[`${type}-${method}`] || `${type} with ${method}`;
};

export const getValueLabel = (type: string, method: string): string => {
    if (type === 'aadhaar-login') return 'Aadhaar Number';
    if (type === 'abha-number') return 'ABHA Number';
    if (type === 'abha-address') return 'ABHA Address';
    if (type === 'enrollment' && method === 'aadhaar') return 'Aadhaar Number';
    return 'Mobile Number';
};

/** Normalize ABHA number to 14 contiguous digits (strip hyphens/spaces/anything non-digit). */
export const normalizeAbhaNumber = (value?: string | null): string => {
    if (!value) return '';
    const digits = value.toString().replace(/\D/g, '');
    return digits.slice(0, 14);
};

/** Normalize gender to male, female, other, unknown as required by FHIR Mapper validation. */
export const normalizeGender = (gender?: string | null): string => {
    if (!gender) return 'unknown';
    const g = gender.trim().toLowerCase();
    if (g === 'm' || g === 'male') return 'male';
    if (g === 'f' || g === 'female') return 'female';
    if (g === 'o' || g === 'other') return 'other';
    return 'unknown';
};

/** Normalize birthDate to YYYY-MM-DD format required by FHIR. */
export const normalizeBirthDate = (dob?: string | null): string => {
    if (!dob) return '1900-01-01';
    const s = dob.trim();

    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // ISO datetime: "2000-06-15T00:00:00.000Z"
    if (s.length > 10 && s[4] === '-' && s[7] === '-') return s.substring(0, 10);

    // DD-MM-YYYY or DD/MM/YYYY
    const dmy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;

    // YYYY-MM (year and month only)
    if (/^\d{4}-\d{1,2}$/.test(s)) {
        const [y, m] = s.split('-');
        return `${y}-${m.padStart(2, '0')}-01`;
    }

    // Bare year like "2000"
    if (/^\d{4}$/.test(s)) return `${s}-01-01`;

    // Last resort: extract any 4-digit year
    const ym = s.match(/(\d{4})/);
    if (ym) return `${ym[1]}-01-01`;

    return '1900-01-01';
};
